<script lang="ts" setup>
import { useLanguage } from '#composables/useLanguage';
import {
  computePresetRange,
  formatRange,
  getAvailablePresets,
  getDefaultCustomRange,
  isPresetAvailable,
  normalizeRangeToGranularity,
  parsePeriodValue,
  serializePeriodValue,
  type DateRangeType,
  type PeriodReferentialIntervalType,
  type PeriodValueType
} from '#domain/core/filter/period';
import { onClickOutside } from '@vueuse/core';
import { computed, ref, watch } from 'vue';
import VueFeather from 'vue-feather';
import { usePeriodReferentialInterval } from './composables/usePeriodReferentialInterval';
import { PERIOD_CUSTOM_OPTION } from './constants/periodField.constant';
import PeriodPopover from './PeriodPopover.vue';
import type { PeriodOptionItemType, PeriodOptionType } from './types/periodField.type';
import type { PeriodFieldPropsType } from './types/periodFieldProps.type';

const { name, modelValue, granularity, withPresets = false, maxDate } = defineProps<PeriodFieldPropsType>();

const emit = defineEmits<{
  'update:modelValue': [value: string];
}>();

const { t } = useLanguage();

const referentialInterval = usePeriodReferentialInterval(() => granularity);

/** The comparison period may not start after the analysis period ends: that boundary caps the refresh state. */
const interval = computed((): PeriodReferentialIntervalType | null => {
  const base = referentialInterval.value;

  if (!base || !maxDate || maxDate.isAfter(base.end)) {
    return base;
  }

  return { start: base.start, end: maxDate.endOf('day') };
});

const selectedOption = ref<PeriodOptionType>(PERIOD_CUSTOM_OPTION);
const customRange = ref<DateRangeType | null>(null);

const options = computed((): PeriodOptionItemType[] => {
  const currentInterval = interval.value;

  if (!granularity || !currentInterval) {
    return [];
  }

  const presets = getAvailablePresets(granularity).map((option) => ({
    value: option,
    label: t(`filters.period.preset.${option}`),
    subLabel: formatRange(computePresetRange(option, granularity, currentInterval), granularity, t)
  }));

  return [...presets, { value: PERIOD_CUSTOM_OPTION, label: t('filters.period.custom') }];
});

const displayedRange = computed((): DateRangeType | null => {
  const option = selectedOption.value;

  if (!granularity || !interval.value) {
    return null;
  }

  return option === PERIOD_CUSTOM_OPTION ? customRange.value : computePresetRange(option, granularity, interval.value);
});

watch([() => modelValue, () => granularity, interval], syncFromModel, { immediate: true });

function syncFromModel() {
  const currentInterval = interval.value;

  if (!granularity || !currentInterval) {
    return;
  }

  const value = parsePeriodValue(modelValue);

  if (value?.kind === 'preset' && withPresets && isPresetAvailable(value.option, granularity)) {
    selectedOption.value = value.option;
    return;
  }

  if (value?.kind === 'custom') {
    selectedOption.value = PERIOD_CUSTOM_OPTION;

    const normalized = normalizeRangeToGranularity(value.range, granularity);
    const isUnchanged = normalized.start.isSame(value.range.start) && normalized.end.isSame(value.range.end);

    customRange.value = normalized;

    // A granularity change (e.g. daily -> weekly) can normalize an already-set custom range: the picker now
    // shows the normalized range, so the parent must be told or the submitted value would silently diverge.
    if (!isUnchanged) {
      emitCurrentValue();
    }
    return;
  }

  // Empty (granularity just changed) or incompatible value: propose the last complete unit
  selectedOption.value = PERIOD_CUSTOM_OPTION;
  customRange.value = getDefaultCustomRange(granularity, currentInterval);
  emitCurrentValue();
}

function currentValue(): PeriodValueType | null {
  const option = selectedOption.value;

  if (option === PERIOD_CUSTOM_OPTION) {
    return customRange.value ? { kind: 'custom', range: customRange.value } : null;
  }

  return { kind: 'preset', option };
}

function emitCurrentValue() {
  const value = currentValue();

  if (value) {
    emit('update:modelValue', serializePeriodValue(value));
  }
}

// --- Popover: the draft is a scratchpad edited freely, only committed to selectedOption/customRange on validate.

const isOpen = ref(false);
const draftOption = ref<PeriodOptionType>(PERIOD_CUSTOM_OPTION);
const draftRange = ref<DateRangeType | null>(null);
const wrapperRef = ref<HTMLElement | null>(null);

onClickOutside(wrapperRef, () => {
  if (isOpen.value) {
    cancel();
  }
});

const chipLabel = computed(() => {
  const range = displayedRange.value;

  const rangeLabel = range
    ? t('filters.period.chip', {
        start: range.start.format('DD/MM/YY'),
        end: range.end.format('DD/MM/YY'),
        interpolation: { escapeValue: false }
      })
    : t('filters.period.placeholder');

  const option = selectedOption.value;

  return option === PERIOD_CUSTOM_OPTION ? rangeLabel : `${t(`filters.period.preset.${option}`)} · ${rangeLabel}`;
});

function open() {
  draftOption.value = selectedOption.value;
  draftRange.value = selectedOption.value === PERIOD_CUSTOM_OPTION ? customRange.value : displayedRange.value;
  isOpen.value = true;
}

function cancel() {
  isOpen.value = false;
}

function validate() {
  selectedOption.value = draftOption.value;

  if (draftOption.value === PERIOD_CUSTOM_OPTION) {
    customRange.value = draftRange.value;
  }

  emitCurrentValue();
  isOpen.value = false;
}

function updateDraftOption(option: PeriodOptionType) {
  draftOption.value = option;

  const currentInterval = interval.value;

  if (!granularity || !currentInterval) {
    return;
  }

  draftRange.value =
    option === PERIOD_CUSTOM_OPTION
      ? (customRange.value ?? getDefaultCustomRange(granularity, currentInterval))
      : computePresetRange(option, granularity, currentInterval);
}

function updateDraftRange(range: DateRangeType) {
  draftRange.value = range;
}
</script>

<template>
  <div class="flex flex-col gap-2 w-full" :data-testid="`period-field-${name}`">
    <p v-if="!granularity || !interval" class="text-sm text-gray-600" data-testid="period-unavailable">
      {{ t('filters.period.unavailableForGranularity') }}
    </p>

    <div v-else ref="wrapperRef">
      <button
        type="button"
        :data-testid="`${name}-chip`"
        class="inline-flex items-center gap-2 px-3 py-1.5 border border-slate-300 rounded-md bg-white text-sm font-medium"
        @click="open"
      >
        {{ chipLabel }}
        <VueFeather type="calendar" class="h-4 w-4 text-blue-600 shrink-0" />
      </button>

      <div v-if="isOpen" class="absolute left-0 top-full z-20 mt-2 w-full max-w-2xl">
        <PeriodPopover
          :name="name"
          :granularity="granularity"
          :interval="interval"
          :with-presets="withPresets"
          :options="options"
          :selected-option="draftOption"
          :range="draftRange"
          @update:selected-option="updateDraftOption"
          @update:range="updateDraftRange"
          @validate="validate"
          @cancel="cancel"
        />
      </div>
    </div>
  </div>
</template>
